import telegramService from './telegram';
import { formatApproxDistance } from '../utils/measures';

/**
 * Geolocation Service
 */

// Ошибка геолокации с машиночитаемым кодом.
// Код важнее текста: по нему решается, показывать ли экран "доступ запрещён".
export class LocationError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'LocationError';
    this.code = code;
  }
}

// Коды ошибок
export const LOCATION_ERROR = {
  DENIED: 'denied',           // пользователь явно отказал — показываем инструкцию
  UNAVAILABLE: 'unavailable', // координаты не удалось получить
  TIMEOUT: 'timeout',         // ответ не пришёл вовремя
  UNSUPPORTED: 'unsupported', // API нет на этом клиенте
};

/**
 * Сколько сбоев подряд терпим, прежде чем показать панель с инструкцией.
 *
 * Позиция теряется на ровном месте: в арке, в метро, в первые секунды
 * после выхода из помещения, пока GPS не прогрелся. Показывать панель
 * на пол-экрана по первому же промаху — значит закрывать человеку карту
 * посреди работающей прогулки. Ждём, пока не отпускает.
 */
export const GEO_GRACE_MS = 45000;

/**
 * Шаг опроса Telegram, пока координаты приходят. Как только промах —
 * цикл останавливается совсем: getLocation на клиенте без доступа сам
 * поднимает системное окно, и автоповторы превращаются в очередь окон.
 * Возобновление — только по действию человека.
 */
export const GEO_POLL_MS = 5000;

/**
 * Паузы перед тихими попытками вернуть слежение после потери сигнала.
 *
 * Повторять getLocation можно только когда координаты уже реально
 * приходили: значит доступ в порядке, и окна на повторе не будет.
 * Флаг isAccessGranted для этого не годится — он отвечает «доступ выдан»
 * и в случае, когда геолокация выключена в самой системе, а координат
 * при этом нет и каждый вызов поднимает окно.
 *
 * Попыток немного и они конечны: если человек выключит геолокацию посреди
 * прогулки, он увидит три окна, а не бесконечную очередь. Дальше слежение
 * возобновляется только по кнопке.
 */
export const GEO_LOST_RETRY_MS = [15000, 30000, 60000];

/**
 * Когда прекращаем попытки совсем. Нужен для старых клиентов Telegram,
 * где у LocationManager нет флагов доступа: там отказ неотличим от
 * «сигнала пока нет», и без этого предела опрос стучался бы вечно.
 * Слежение поднимется обратно кнопкой «Попробовать снова».
 */
export const GEO_GIVEUP_MS = 180000;

/**
 * Сколько ждём ответа на один getLocation. Telegram иногда не вызывает
 * колбэк вовсе — пока открыто другое его окно или приложение свёрнуто.
 * Без предела запрос «висел» вечно, опрос молча вставал, и метка
 * застывала на месте
 */
export const GEO_RESPONSE_TIMEOUT_MS = 15000;

/**
 * Позиция старше этого — уже не «живое» слежение: кнопка геолокации
 * и возврат в приложение перезапускают его, а не верят флагу «идёт»
 */
export const GEO_STALE_MS = 20000;

/**
 * Откуда брать координаты на прогулке.
 *
 * Telegram для Android отвечает на getLocation последней точкой,
 * которую запомнила система (`getLastKnownLocation`), и свежих
 * у неё не просит. Опрос возвращал одну и ту же стартовую точку —
 * метка стояла, сколько ни иди. Баг клиента, открыт с 2024:
 * github.com/Telegram-Mini-Apps/issues/issues/56. Поэтому на Android
 * слежение ведёт браузерный watchPosition — WebView Telegram отдаёт
 * через него настоящий GPS.
 *
 * @returns {'telegram'|'browser'|'none'}
 */
export const pickGeoSource = (lm, platform, hasBrowserGeo) => {
  if (!lm) return hasBrowserGeo ? 'browser' : 'none';
  if ((platform === 'android' || platform === 'android_x') && hasBrowserGeo) return 'browser';
  return 'telegram';
};

/**
 * Разбирает неудачную попытку определить позицию: сама ли причина
 * рассосётся и что показывать человеку.
 *
 * `terminal: true` — не рассосётся (запрет, нет поддержки): опрос
 * останавливаем и панель показываем сразу, ждать нечего.
 * `terminal: false` — сбой временный: продолжаем попытки и молчим,
 * пока не выйдет GEO_GRACE_MS.
 *
 * @param {GeolocationPositionError|undefined|null} err ошибка браузера;
 *        undefined/null, когда Telegram вернул null и кода не дал
 * @param {object|null} lm LocationManager Telegram, если прогулка в нём
 * @param {boolean} everGranted доступ подтверждён — координаты уже
 *        приходили или клиент прямо говорит, что доступ выдан
 */
export const classifyGeoFailure = (err, lm, everGranted = false) => {
  if (lm) {
    // Telegram кода ошибки не даёт — getLocation просто возвращает null.
    // Зато с Bot API 8.0 у LocationManager есть флаги, по которым видно,
    // отказал человек или фикса пока нет
    if (lm.isLocationAvailable === false) {
      return { terminal: true, kind: LOCATION_ERROR.UNSUPPORTED };
    }
    if (lm.isAccessRequested && !lm.isAccessGranted) {
      return { terminal: true, kind: LOCATION_ERROR.DENIED };
    }
    // Доступ не подтверждён — значит повторять нельзя. getLocation
    // на клиенте без выданного доступа поднимает системный запрос
    // Telegram, и опрос по кругу превращался в окно каждые 15–20 секунд.
    // Спрашиваем один раз; вернуться к этому можно кнопкой геолокации
    if (!everGranted) {
      return { terminal: true, kind: LOCATION_ERROR.DENIED };
    }
    // Доступ выдан, координат нет — потеря сигнала. Здесь повторы
    // безопасны: окна они не поднимают
    return { terminal: false, kind: LOCATION_ERROR.UNAVAILABLE };
  }

  if (!navigator.geolocation) {
    return { terminal: true, kind: LOCATION_ERROR.UNSUPPORTED };
  }
  // 1 = PERMISSION_DENIED. Коды 2 (POSITION_UNAVAILABLE) и 3 (TIMEOUT)
  // приходят и когда всё настроено верно, просто нет сигнала
  if (err?.code === 1) {
    return { terminal: true, kind: LOCATION_ERROR.DENIED };
  }
  return { terminal: false, kind: LOCATION_ERROR.UNAVAILABLE };
};

// Сколько ждём ответа Telegram перед тем, как считать запрос зависшим
const TELEGRAM_INIT_TIMEOUT = 5000;
const TELEGRAM_LOCATION_TIMEOUT = 15000;

// LocationManager.init() асинхронный: до колбэка менеджер не готов и
// getLocation() возвращает null. Ждём готовности, иначе первый запрос
// всегда падает, а повторный проходит.
const initLocationManager = (locationManager) => {
  return new Promise((resolve, reject) => {
    if (locationManager.isInited) {
      resolve();
      return;
    }

    let settled = false;

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new LocationError(LOCATION_ERROR.UNAVAILABLE, 'LocationManager init timed out'));
    }, TELEGRAM_INIT_TIMEOUT);

    try {
      locationManager.init(() => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve();
      });
    } catch (error) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(new LocationError(LOCATION_ERROR.UNAVAILABLE, error.message));
    }
  });
};

// Запрос координат через Telegram LocationManager
const requestTelegramLocation = async () => {
  const tg = telegramService.getTelegramWebApp();
  const locationManager = tg?.LocationManager;

  // LocationManager появился только в Bot API 8.0
  if (!locationManager) {
    throw new LocationError(LOCATION_ERROR.UNSUPPORTED, 'LocationManager not available');
  }

  await initLocationManager(locationManager);

  // Доступ уже запрашивали и получили отказ — не дёргаем getLocation впустую
  if (locationManager.isAccessRequested && !locationManager.isAccessGranted) {
    throw new LocationError(LOCATION_ERROR.DENIED, 'User denied location access');
  }

  return new Promise((resolve, reject) => {
    let settled = false;

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new LocationError(LOCATION_ERROR.TIMEOUT, 'Location request timed out'));
    }, TELEGRAM_LOCATION_TIMEOUT);

    try {
      locationManager.getLocation((location) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);

        if (location && location.latitude != null && location.longitude != null) {
          resolve({
            latitude: location.latitude,
            longitude: location.longitude,
          });
          return;
        }

        // null приходит и при отказе, и при технической ошибке.
        // Отличаем одно от другого по флагу доступа.
        if (locationManager.isAccessGranted === false) {
          reject(new LocationError(LOCATION_ERROR.DENIED, 'User denied location access'));
        } else {
          reject(new LocationError(LOCATION_ERROR.UNAVAILABLE, 'Location information unavailable'));
        }
      });
    } catch (error) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(new LocationError(LOCATION_ERROR.UNAVAILABLE, error.message));
    }
  });
};

// Запрос координат через браузер (DEV-режим и старые клиенты Telegram)
const requestBrowserLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new LocationError(LOCATION_ERROR.UNSUPPORTED, 'Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            reject(new LocationError(LOCATION_ERROR.DENIED, 'User denied location access'));
            break;
          case error.POSITION_UNAVAILABLE:
            reject(new LocationError(LOCATION_ERROR.UNAVAILABLE, 'Location information unavailable'));
            break;
          case error.TIMEOUT:
            reject(new LocationError(LOCATION_ERROR.TIMEOUT, 'Location request timed out'));
            break;
          default:
            reject(new LocationError(LOCATION_ERROR.UNAVAILABLE, 'Unknown location error'));
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  });
};

// Request user location
export const requestLocation = async () => {
  if (!telegramService.isRunningInTelegramApp()) {
    return requestBrowserLocation();
  }

  try {
    return await requestTelegramLocation();
  } catch (error) {
    // На клиентах без LocationManager пробуем браузерный API
    if (error.code === LOCATION_ERROR.UNSUPPORTED) {
      return requestBrowserLocation();
    }
    throw error;
  }
};

// Calculate distance between two coordinates using Haversine formula
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3; // Earth radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const distance = R * c; // Distance in meters

  return distance;
};

/**
 * Расстояние до места, готовое к показу. Принимает **метры**.
 *
 * Округление и знак «~» — в `formatApproxDistance`: расчёт идёт
 * по прямой (гаверсинус), а не по пешему пути, поэтому «347 м»
 * обещали точность, которой нет — GPS врёт на 10–50 м, а через
 * Неву дорога бывает втрое длиннее. Брендбук 6.2.
 *
 * Второй копии этой функции больше нет: в `utils/maps.js` лежала
 * такая же по имени и виду результата, но принимавшая километры —
 * перепутать их значило показать «1,2 км» как «1 м».
 */
export const formatDistance = (meters) => formatApproxDistance(Number(meters) / 1000);

// Sort places by distance from user location
export const sortPlacesByDistance = (places, userLat, userLon) => {
  // Add distance to each place
  const placesWithDistance = places.map((place) => {
    // Get place coordinates
    const placeLat = parseFloat(place.latitude);
    const placeLon = parseFloat(place.longitude);

    // Skip if place doesn't have coordinates
    if (!placeLat || !placeLon) {
      return {
        ...place,
        distance: Infinity,
      };
    }

    // Calculate distance
    const distance = calculateDistance(userLat, userLon, placeLat, placeLon);

    return {
      ...place,
      distance,
    };
  });

  // Sort by distance (ascending)
  placesWithDistance.sort((a, b) => a.distance - b.distance);

  return placesWithDistance;
};

// Check if coordinates are valid
export const isValidCoordinates = (latitude, longitude) => {
  return (
    latitude !== null &&
    longitude !== null &&
    !isNaN(latitude) &&
    !isNaN(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
  );
};

const geolocationService = {
  requestLocation,
  calculateDistance,
  formatDistance,
  sortPlacesByDistance,
  isValidCoordinates,
  LocationError,
  LOCATION_ERROR,
};

export default geolocationService;
